import React from 'react';
import 'twin.macro';
import {
  Periode,
  Item,
  Matiere as IMatiere,
} from '@teetsh/app/src/types/programmations';
import { HeaderItem } from '@teetsh/app/src/containers/Programmations/ProgV2/Print/components/HeaderItem';
import { MatiereLayout } from './components/MatiereLayout';
import { IRow, Row } from './components/Row';
import _orderBy from '@teetsh/app/src/utils/arrays/orderBy';
import { RowTitleOrientation } from '@teetsh/app/src/containers/Programmations/ProgV2/Print/types';
import { Filler, Header, IHeader } from './types';
import { getPureReferentialItemName } from '@teetsh/app/src/components/Bo/ReferentialItemElement';

export function buildRows(
  matieres: IMatiere[],
  periodes: Periode[],
  enforceNbColumns?: number,
): IRow[] {
  return periodes.map(p => {
    const blocks = matieres.map(m => {
      const items: Item[] = m.domaines.flatMap(d =>
        d.periodContents
          .filter(pc => pc.periodeId === p.id)
          .flatMap(v => _orderBy(v.items, 'y', ['asc']))
          .map(i => ({
            ...i,
            domaine: {
              ...d,
              name: getPureReferentialItemName(d.name),
            },
          })),
      );
      return { items };
    });

    const additionalBlocks = enforceNbColumns
      ? Array.from<{ items: Item[] }>({
          length: enforceNbColumns - matieres.length,
        }).fill({ items: [] })
      : [];

    return {
      header: {
        color: p.color,
        name: p.name,
        details: p.subtitle,
      },
      subrows: [
        {
          blocks: [...blocks, ...additionalBlocks],
        },
      ],
    };
  });
}

function buildHeaders(
  matieres: IMatiere[],
  enforceNbColumns?: number,
): IHeader[] {
  const headers: Header[] = matieres.map(m => ({
    type: 'header',
    name: m.name,
    color: m.color,
  }));
  const fillers = enforceNbColumns
    ? Array.from<Filler>({ length: enforceNbColumns - matieres.length }).fill({
        type: 'filler',
      })
    : [];
  return [...headers, ...fillers];
}

interface Props {
  matieres: IMatiere[];
  periodes: Periode[];
  enforceNbColumns?: number;
  rowTitleOrientation: RowTitleOrientation;
  isFirst: boolean;
  preventCutting: boolean;
  breakBefore: boolean;
}

export function MatierePeriodeView(props: Props) {
  const { matieres, periodes, enforceNbColumns, rowTitleOrientation } = props;
  const hasPeriodeSubtitles = periodes.some(p => p.subtitle);
  const rows = buildRows(matieres, periodes, enforceNbColumns);
  const headers = buildHeaders(matieres, enforceNbColumns);

  const areRowsEmpty = rows.every(r =>
    r.subrows.every(s =>
      (s.blocks || []).every(b => (b?.items || []).length === 0),
    ),
  );
  // Dans le cas d'un premier groupe même si c'est vide on affiche quand même
  // la structure, ça semble mieux que juste du vide
  if (areRowsEmpty && !props.isFirst) {
    return null;
  }

  return (
    <MatiereLayout
      hasFirstColSubtitles={hasPeriodeSubtitles}
      rowTitleOrientation={rowTitleOrientation}
      breakBefore={props.breakBefore && !props.isFirst}
    >
      {headers.map((m, idx) => {
        if (m.type === 'filler') {
          return <div key={idx} tw="flex-1 min-w-0" />;
        }
        return (
          <div key={idx} tw="flex-1 min-w-0">
            <HeaderItem value={m.name} color={m.color} />
          </div>
        );
      })}
      {rows.map((r, idx) => (
        <Row
          key={idx}
          subrows={r.subrows}
          header={r.header}
          hasFirstColSubtitles={hasPeriodeSubtitles}
          rowTitleOrientation={rowTitleOrientation}
          preventCutting={props.preventCutting}
          breakBefore={props.breakBefore}
        />
      ))}
    </MatiereLayout>
  );
}
